// The Atelier's centre column: session header, transcript (the admin's messages, Claude's text as
// Markdown, compact step lines such as "✎ web/ui/topbar.js" or "▶ npm test") appended live
// without re-rendering what is already on screen, a "Claude travaille…" indicator, and the
// composer (Cmd/Ctrl+Enter sends). Text always goes in through text nodes / renderMarkdown.
import { h, button, badge, spinner, kbd, icon, uid } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { renderMarkdown, formatTime, formatDateTime, relativeTime } from '../../format.js';
import { stateInfo, messageKey, stepView, validateMessage, MAX_MESSAGE_CHARS } from './model.js';
import './strings.js';

/** Distance from the bottom under which the transcript keeps following new messages. */
const FOLLOW_PX = 120;

/**
 * Creates the conversation column.
 * @param {object} ctx app context
 * @param {{onSend: (text: string) => Promise<boolean>}} handlers resolves true when sent
 * @returns {{el: HTMLElement, show(session: object|null, opts?: object): void, sync(session: object|null, opts?: object): void,
 *   setBlocker(text: string): void, focus(): void, getDraft(): string, setDraft(text: string): void, announce(text: string): void}}
 */
export function createConversation(ctx, { onSend }) {
  let session = null;
  let renderedFor = undefined;
  let renderedKeys = [];
  let lastGroup = null; // { type, list } of the last rendered group
  let sending = false;
  let blocker = '';

  const titleEl = h('h3', { class: 'atl-chat-title' });
  const badgeSlot = h('span', { class: 'atl-chat-badge' });
  const metaEl = h('p', { class: 'atl-chat-meta mono-label' });
  const header = h('header', { class: 'atl-chat-head' }, h('div', { class: 'atl-chat-titles' }, titleEl, metaEl), badgeSlot);
  const transcript = h('div', { class: 'atl-transcript', role: 'log', 'aria-live': 'off', 'aria-label': t('atl.view.chat'), tabIndex: 0 });
  const working = h('div', { class: 'atl-working', hidden: true });
  const errorEl = h('p', { class: 'atl-chat-error', role: 'alert', hidden: true });
  const live = h('p', { class: 'sr-only', 'aria-live': 'polite' });

  const inputId = uid('atl-input');
  const area = h('textarea', {
    id: inputId, class: 'input textarea atl-input', rows: 3, maxLength: MAX_MESSAGE_CHARS,
    placeholder: t('atl.chat.placeholder'), spellcheck: true, 'aria-describedby': `${inputId}-hint ${inputId}-why`,
  });
  area.value = '';
  const counter = h('span', { class: 'atl-counter mono-label', 'aria-hidden': 'true' });
  const whyEl = h('p', { class: 'atl-why', id: `${inputId}-why`, hidden: true });
  const sendBtn = button(t('atl.chat.send'), { variant: 'primary', icon: 'send', type: 'submit' });
  const form = h('form', { class: 'atl-composer', novalidate: true, onSubmit: (e) => { e.preventDefault(); submit(); } },
    h('label', { class: 'sr-only', htmlFor: inputId }, t('atl.chat.label')),
    area,
    h('div', { class: 'atl-composer-foot' },
      h('p', { class: 'atl-hint muted', id: `${inputId}-hint` }, kbd('⌘'), ' / ', kbd('Ctrl'), ' + ', kbd('Entrée'), ' ',
        h('span', { class: 'sr-only' }, t('atl.chat.shortcut'))),
      counter, sendBtn),
    whyEl);
  const el = h('section', { class: 'atl-chat', 'aria-label': t('atl.view.chat') }, header, transcript, working, errorEl, form, live);

  area.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !e.isComposing) {
      e.preventDefault();
      submit();
    }
  });
  area.addEventListener('input', () => { updateCounter(); autosize(); });

  function updateCounter() {
    const n = area.value.length;
    counter.textContent = n > MAX_MESSAGE_CHARS * 0.8 ? t('atl.chat.counter', { n, max: MAX_MESSAGE_CHARS }) : '';
  }

  function autosize() {
    if (!area.style || typeof area.scrollHeight !== 'number') return;
    area.style.setProperty('height', 'auto');
    area.style.setProperty('height', `${Math.min(Math.max(area.scrollHeight, 72), 280)}px`);
  }

  async function submit() {
    if (sending || blocker) return;
    const res = validateMessage(area.value);
    if (!res.ok) {
      whyEl.textContent = res.error;
      whyEl.hidden = false;
      area.focus();
      return;
    }
    sending = true;
    syncControls();
    try {
      const ok = await onSend(res.value);
      if (ok) {
        area.value = '';
        updateCounter();
        autosize();
      }
    } finally {
      sending = false;
      syncControls();
    }
  }

  function syncControls() {
    const blocked = !!blocker;
    area.disabled = blocked && !area.value;
    sendBtn.disabled = blocked || sending;
    sendBtn.classList.toggle('is-busy', sending);
    whyEl.textContent = blocker;
    whyEl.hidden = !blocker;
    sendBtn.querySelector?.('.btn-label')?.replaceChildren?.(session ? t('atl.chat.send') : t('atl.chat.start'));
    area.placeholder = session ? t('atl.chat.placeholderFollow') : t('atl.chat.placeholder');
  }

  function nearBottom() {
    const { scrollHeight = 0, scrollTop = 0, clientHeight = 0 } = transcript;
    return scrollHeight - scrollTop - clientHeight < FOLLOW_PX;
  }

  function scrollToEnd() {
    if (typeof transcript.scrollHeight === 'number') transcript.scrollTop = transcript.scrollHeight;
  }

  // --- transcript -----------------------------------------------------------------------------
  function msgTime(m) {
    return m.at ? h('time', { class: 'atl-msg-time', datetime: String(m.at), title: formatDateTime(m.at) }, formatTime(m.at)) : null;
  }

  function messageEl(m) {
    if (m.role === 'user') {
      return h('article', { class: 'atl-msg is-user' },
        h('header', { class: 'atl-msg-head mono-label' }, t('atl.chat.you'), msgTime(m)),
        h('div', { class: 'atl-msg-text' }, m.text));
    }
    return h('article', { class: 'atl-msg is-claude' },
      h('header', { class: 'atl-msg-head mono-label' }, h('span', { class: 'atl-claude-mark', 'aria-hidden': 'true' }, icon('sparkle', { size: 14 })), t('atl.chat.claude'), msgTime(m)),
      h('div', { class: 'md atl-msg-md' }, renderMarkdown(m.text)));
  }

  function stepEl(m) {
    const v = stepView(m.text);
    const text = v.kind === 'fail' && !v.text.startsWith('✗') ? `✗ ${v.text}` : v.text;
    return h('li', { class: ['atl-step', `is-${v.kind}`], title: m.at ? formatDateTime(m.at) : null },
      h('span', { class: 'atl-step-text' }, text));
  }

  function appendOne(m) {
    if (m.role === 'step') {
      if (lastGroup?.type !== 'steps') {
        const list = h('ul', { class: 'atl-steps-list' });
        transcript.appendChild(h('div', { class: 'atl-steps' }, list));
        lastGroup = { type: 'steps', list };
      }
      lastGroup.list.appendChild(stepEl(m));
    } else {
      transcript.appendChild(messageEl(m));
      lastGroup = { type: m.role };
    }
    renderedKeys.push(messageKey(m));
  }

  function renderTranscript(messages) {
    transcript.replaceChildren();
    renderedKeys = [];
    lastGroup = null;
    if (!session) {
      transcript.appendChild(newSessionIntro());
      return;
    }
    for (const m of messages) appendOne(m);
  }

  function newSessionIntro() {
    const example = (key) => h('li', {}, h('button', {
      type: 'button', class: 'atl-example',
      onClick: () => { area.value = t(key); updateCounter(); autosize(); area.focus(); },
    }, t(key)));
    return h('div', { class: 'atl-new' },
      h('div', { class: 'atl-new-icon', 'aria-hidden': 'true' }, icon('hammer', { size: 22 })),
      h('h4', { class: 'atl-new-title' }, t('atl.chat.newTitle')),
      h('p', { class: 'atl-new-text' }, t('atl.chat.newText')),
      h('p', { class: 'atl-new-label mono-label' }, t('atl.chat.examples')),
      h('ul', { class: 'atl-examples' }, example('atl.chat.example.1'), example('atl.chat.example.2'), example('atl.chat.example.3')));
  }

  /** Appends what is new, or re-renders when the transcript is not a continuation. */
  function syncTranscript() {
    const messages = Array.isArray(session?.messages) ? session.messages : [];
    const id = session?.id ?? null;
    const follow = nearBottom();
    let continuation = renderedFor === id && messages.length >= renderedKeys.length;
    if (continuation) {
      for (let i = 0; i < renderedKeys.length; i++) {
        if (messageKey(messages[i]) !== renderedKeys[i]) { continuation = false; break; }
      }
    }
    if (!continuation) {
      renderedFor = id;
      renderTranscript(messages);
      scrollToEnd();
      return;
    }
    const added = messages.slice(renderedKeys.length);
    for (const m of added) appendOne(m);
    if (added.length && (follow || added.some((m) => m.role === 'user'))) scrollToEnd();
  }

  function renderHeader() {
    if (!session) {
      titleEl.textContent = t('atl.chat.newTitle');
      metaEl.textContent = '';
      badgeSlot.replaceChildren();
      working.hidden = true;
      errorEl.hidden = true;
      return;
    }
    titleEl.textContent = session.title || t('atl.chat.newTitle');
    const bits = [];
    if (session.branch) bits.push(String(session.branch));
    if (session.createdAt) bits.push(relativeTime(session.createdAt));
    if (session.createdByName) bits.push(t('atl.sessions.by', { name: session.createdByName }));
    metaEl.textContent = bits.join(' · ');
    const info = stateInfo(session);
    badgeSlot.replaceChildren(badge(info.label, info.tone, { class: info.live ? 'atl-live-badge' : '' }));
    const busyText = session.busy === 'apply' ? t('atl.chat.applying')
      : session.busy === 'discard' ? t('atl.chat.discarding')
        : session.state === 'running' ? t('atl.chat.working')
          : session.state === 'testing' ? t('atl.chat.testing') : '';
    working.hidden = !busyText;
    working.replaceChildren(...(busyText ? [spinner(busyText), h('div', { class: 'atl-working-text' },
      h('strong', {}, busyText), session.state === 'running' ? h('span', { class: 'muted' }, t('atl.chat.workingHint')) : null)] : []));
    errorEl.hidden = !(session.state === 'failed' && session.error);
    errorEl.textContent = session.state === 'failed' && session.error ? t('atl.chat.error', { error: session.error }) : '';
  }

  return {
    el,
    /** Shows a session (or the new-session screen when null), re-rendering everything. */
    show(next, { blocker: why = '' } = {}) {
      session = next || null;
      renderedFor = undefined;
      renderHeader();
      syncTranscript();
      blocker = why;
      syncControls();
    },
    /** Updates with a newer version of the same session (live events, reloads). */
    sync(next, { blocker: why = blocker } = {}) {
      session = next || null;
      renderHeader();
      syncTranscript();
      blocker = why;
      syncControls();
    },
    setBlocker(why) {
      blocker = why || '';
      syncControls();
    },
    focus() {
      if (!area.disabled) area.focus();
      else transcript.focus?.();
    },
    getDraft: () => String(area.value ?? ''),
    setDraft(text) {
      area.value = text || '';
      updateCounter();
      autosize();
      syncControls();
    },
    announce(text) {
      live.textContent = '';
      live.textContent = text || '';
    },
  };
}
